/**
 * The one product grid, shared by every place that lays products out.
 *
 * ## Why this exists
 *
 * The shop grid, the homepage showcases and their loading skeletons each
 * declared their own column counts. All three had the same hole in them:
 *
 *     grid-cols-2 ... lg:grid-cols-4
 *
 * Nothing between `sm` (640px) and `lg` (1024px). Every tablet in that range
 * therefore rendered TWO columns across 834 pixels — cards roughly 390px
 * wide, one card taking nearly half the viewport, with the vertical rhythm
 * stretched to match. What reads as "the images are too big and there is too
 * much space" is not a sizing decision anywhere; it is a missing breakpoint,
 * and it was missing three times because the class list was copied three
 * times.
 *
 * Declaring it once means the next person to change the rhythm changes it
 * everywhere, and a skeleton can never disagree with the grid it stands in
 * for — a mismatch there causes the layout to jump at the exact moment the
 * content arrives.
 *
 * ## The ramp
 *
 *   base   2 columns — phones
 *   sm     4 columns — everything above 640px
 *
 * ## Why the showcase ramp skips three
 *
 * The homepage showcases always request exactly FOUR products, so a
 * three-column band leaves the fourth card stranded alone on a second row —
 * a widow that makes a curated row look like a loading error. Two and four
 * both divide four exactly, so the row is always full at every width. The
 * shop listing has no fixed count and uses the ramp below instead.
 */
export const PRODUCT_GRID_CLASS = 'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-4 sm:gap-x-5 lg:gap-x-6';

/**
 * The listing ramp, shaped around the filter rail: 2 / 3 / 4 / 3 / 4.
 *
 * Used where the number of products is arbitrary, so an incomplete final row
 * is expected and reads as "the end of the results" rather than as a widow.
 *
 * The count DROPS at `lg`, which looks like a mistake and is not. The filter
 * rail is `hidden lg:block`, so the tablet band has the entire width to
 * itself and can carry four columns at ~185px each; the moment the rail
 * appears it takes 15rem out of the row, and four columns there would squeeze
 * the cards smaller than they are on a phone. Three, then four again at `xl`
 * once the width has been made up.
 *
 * Tie this to the rail: if `ShopPage`'s aside ever changes breakpoint, this
 * has to change with it.
 */
export const PRODUCT_GRID_NARROW_CLASS = 'grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-5 md:grid-cols-4 lg:grid-cols-3 lg:gap-x-6 xl:grid-cols-4';
